/**
 * UserProfileManager.js
 * ═══════════════════════════════════════════════════════════════════════════════
 * Centralized user profile management with localStorage caching (24-hour TTL)
 * 
 * Features:
 * - Fetches user profile once and caches in localStorage
 * - Automatic 24-hour expiration (TTL)
 * - Auto-refetches when expired
 * - No manual logout handler needed
 * 
 * Usage:
 * import UserProfileManager from './services/UserProfileManager';
 * 
 * // Initialize at app startup
 * await UserProfileManager.initialize(ssoToken);
 * 
 * // Access profile anywhere
 * const profile = UserProfileManager.getProfile();
 * console.log(profile.jobTitle, profile.department);
 * 
 * // Optional: Force refresh
 * UserProfileManager.refresh(ssoToken);
 * 
 * // Optional: Clear on logout (not required - TTL handles expiration)
 * UserProfileManager.clearProfile();
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import ApiClient from "./ApiClient";

class UserProfileManager {
    constructor() {
        this.storageKey = 'spark_user_profile';
        this.profile = null;
        this.maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    }

    /**
     * Initialize - fetch user profile and cache in localStorage (24-hour TTL)
     * Call this once during app initialization (in router.js)
     */
    async initialize() {
        try {
            console.log("[UserProfile] Initializing...");

            const cached = this.loadFromStorage();
            if (cached) {
                this.profile = cached;
                console.log("[UserProfile] Loaded from cache");
                return cached;
            }

            console.log("[UserProfile] Fetching from API...");
            const profileData = await ApiClient.get("/api/user/profile");

            if (!profileData || !profileData.id) {
                console.warn("[UserProfile] Invalid profile data");
                return null;
            }

            this.profile = profileData;
            this.saveToStorage(this.profile);

            console.log("[UserProfile] Profile loaded:", profileData.displayName);
            return this.profile;
        } catch (error) {
            console.warn("[UserProfile] Error:", error.message || error);
            return null;
        }
    }

    /**
     * Get current user profile (from memory or localStorage)
     */
    getProfile() {
        // Return from memory if available
        if (this.profile) {
            return this.profile;
        }

        // Try loading from localStorage (checks TTL automatically)
        const cached = this.loadFromStorage();
        if (cached) {
            this.profile = cached;
            return cached;
        }

        // Not available - needs initialization
        return null;
    }

    /**
     * Get specific profile field (with fallback)
     */
    getField(fieldName, fallback = null) {
        const profile = this.getProfile();
        if (!profile) return fallback;

        // Support nested fields like 'manager.displayName'
        if (fieldName.includes('.')) {
            const parts = fieldName.split('.');
            let value = profile;
            for (const part of parts) {
                value = value?.[part];
                if (value === undefined) return fallback;
            }
            return value || fallback;
        }

        return profile[fieldName] || fallback;
    }

    /**
     * Refresh profile (force refetch from backend)
     */
    async refresh() {
        console.log("[UserProfile] Refreshing...");
        this.clearProfile();
        return await this.initialize();
    }

    /**
     * Clear profile from memory and localStorage
     * Optional - TTL handles expiration automatically
     */
    clearProfile() {
        this.profile = null;
        try {
            localStorage.removeItem(this.storageKey);
        } catch (error) {
            console.warn('[UserProfile] Could not clear localStorage:', error.message);
        }
    }

    /**
     * Save profile to localStorage with timestamp and TTL metadata
     */
    saveToStorage(profile) {
        try {
            const data = {
                profile: profile,
                timestamp: Date.now(),
                version: 1,
                expiresAt: Date.now() + this.maxAge
            };
            localStorage.setItem(this.storageKey, JSON.stringify(data));
        } catch (error) {
            console.warn('[UserProfile] Could not save to localStorage:', error.message);
        }
    }

    /**
     * Load profile from localStorage (with automatic 24-hour TTL check)
     * Returns null if expired (and removes from storage)
     */
    loadFromStorage() {
        try {
            const raw = localStorage.getItem(this.storageKey);
            if (!raw) return null;

            const data = JSON.parse(raw);

            // Check expiration (24 hours TTL)
            const age = Date.now() - (data.timestamp || 0);
            const isExpired = age > this.maxAge;

            if (isExpired) {
                console.log('[UserProfile] Cache expired - will refetch');
                localStorage.removeItem(this.storageKey);
                return null;
            }

            return data.profile;
        } catch (error) {
            console.warn('[UserProfile] Could not load from localStorage:', error.message);
            // Clear corrupted data
            try {
                localStorage.removeItem(this.storageKey);
            } catch (e) {
                // Ignore
            }
            return null;
        }
    }

    /**
     * Check if profile is available (not expired)
     */
    isLoaded() {
        return this.profile !== null || this.loadFromStorage() !== null;
    }

    /**
     * Get cache statistics (for debugging)
     */
    getCacheInfo() {
        try {
            const raw = localStorage.getItem(this.storageKey);
            if (!raw) return { cached: false };

            const data = JSON.parse(raw);
            const age = Date.now() - (data.timestamp || 0);
            const remaining = this.maxAge - age;

            return {
                cached: true,
                ageHours: Math.floor(age / (60 * 60 * 1000)),
                expiresInHours: Math.floor(remaining / (60 * 60 * 1000)),
                isExpired: age > this.maxAge
            };
        } catch (error) {
            return { cached: false, error: error.message };
        }
    }
}

// Export singleton instance
const userProfileManager = new UserProfileManager();
export default userProfileManager;